/* eslint react-hooks/exhaustive-deps: "warn" */
import { type CSSProperties, useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { Button } from 'ui/atoms/Button/Button';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';

import styles from './TechnologyFields.module.scss';

type Translate = (key: string) => string;

const CHECK_ICON = (
  <svg viewBox="0 0 16 16" fill="none">
    <path d="M3.5 8.3 6.4 11l6-6.4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

type Category = {
  number: string;
  title: string;
  items: string[];
};

const getCategories = (t: Translate): Category[] => [
  {
    number: '01',
    title: t('Company registry data'),
    items: [
      t('Legal name & previous names'),
      t('Registration number, VAT & tax IDs'),
      t('Legal form & incorporation date'),
      t('Company status'),
      t('Registered & trading addresses'),
      t('Registry source & filing link'),
    ],
  },
  {
    number: '02',
    title: t('Financials'),
    items: [
      t('Filed revenue & turnover'),
      t('Assets, liabilities & net assets'),
      t('Profit & loss figures'),
      t('Filing history & latest accounts'),
      t('Currency & reporting period'),
      t('Multi-year financial trend'),
    ],
  },
  {
    number: '03',
    title: t('Firmographics'),
    items: [
      t('Industry codes — SIC, NACE, NAICS'),
      t('Employee counts & size bands'),
      t('Company age & trading status'),
      t('Websites & digital footprint'),
      t('Phone numbers'),
      t('Geographic & jurisdiction data'),
    ],
  },
  {
    number: '04',
    title: t('Directors & officers'),
    items: [
      t('Directors & active officers'),
      t('Officer role & position'),
      t('Appointment & resignation dates'),
      t('Nationality & country of residence'),
      t('Date of birth (where filed)'),
      t('Occupation'),
    ],
  },
  {
    number: '05',
    title: t('Shareholders & ownership'),
    items: [
      t('Shareholders & shareholdings'),
      t('Share classes & percentages'),
      t('Beneficial owners (PSCs / UBOs)'),
      t('Ownership percentages'),
      t('Ultimate beneficial owner chain'),
    ],
  },
  {
    number: '06',
    title: t('Group structure'),
    items: [
      t('Immediate parent'),
      t('Global ultimate owner'),
      t('Subsidiaries & branches'),
      t('Corporate linkage across registries'),
      t('Cross-registry entity resolution'),
    ],
  },
];

export const TechnologyFields = () => {
  const { t } = useInt();
  const categories = getCategories(t);
  const gridRef = useRef<HTMLDivElement>(null);
  const revealed = useIntersection(gridRef, { threshold: 0.1 });

  return (
    <section className={styles.root}>
      <div className={styles.grid}>
        <div className={styles.gut}>
          <span>{t('fields')}</span>
        </div>

        <div className={styles.col}>
          <TitleBlock
            align="left"
            title={t('Every field on the record, sourced')}
            description={t(
              '147 documented fields per company, in six groups. Each one read from the register it was filed with, carrying its own register, reference and timestamp. Modelled values are labelled as modelled.',
            )}
          />

          <div className={cn(styles.categories, revealed && styles.categoriesOn)} ref={gridRef}>
            {categories.map((category, i) => (
              <div className={styles.category} style={{ '--d': `${i * 60}ms` } as CSSProperties} key={category.title}>
                <span className={styles.ghostN} aria-hidden="true">
                  {category.number}
                </span>

                <span className={styles.n}>{category.number}</span>
                <h3 className={styles.categoryTitle}>{category.title}</h3>
                <ul className={styles.list}>
                  {category.items.map((item) => (
                    <li key={item}>
                      <span className={styles.check}>{CHECK_ICON}</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className={styles.foot}>
            <Button type="text" size="lg" href="/data-dictionary" suffix="→">
              {t('View the data dictionary')}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
